import { useContext, useState } from 'react';
import { AuthContext, ProfileContext }  from '../../App';
import { useTranslation } from 'react-i18next';
import { Button, Collapse, Modal } from 'antd';
import { SelectFormField } from '../FormFields';
import { PostResponse } from '../Models';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { IMaskInput }  from 'react-imask';
import { SuccessNotification } from '../Notification';
import { putJson } from '../../util/HttpRequest';
import {CacheKey}  from '../../util/Cache';
import { formatPin } from '../../util/Format';

type FieldLength = {
    addressCity: number,
    addressLine1: number,
    addressLine2: number,
    addressPostalCode: number,
    addressStateCode: number,
    addressStateName: number,
    addressZipMax: number,
    addressZipMin: number,
    claimNumber: number,
    insuredName: number,
    loanNumber: number,
    mobilePhone: number,
    pin: number
}

function RegistrationUpdate(props) {
    const phoneNumberMaskRegExp = new RegExp(`[() _-]+`, 'g');
    const pinMaskRegExp = new RegExp(`[_]+`, 'g');
    const zipMaskRegExp = new RegExp(`[_-]+`, 'g');
    const [errorMsg, setErrorMsg] = useState("");
    const [activeCollapseKey, setActiveCollapseKey] = useState<string | string[]>('p'); //since the defaultActiveKey = p for Static Collapse items,the the active key is also p. 
    const {accessToken, extendSession} = useContext(AuthContext);
    const {refreshUserProfile, userProfile} = useContext(ProfileContext);
    const { t } = useTranslation(["ClaimRegistration", "Form", "Language", "Profile"]);

    const fieldLength: FieldLength = {
        addressCity: 25,
        addressLine1: 50,
        addressLine2: 50,
        addressPostalCode: 10,
        addressStateCode: 2,
        addressStateName: 25,
        addressZipMin: 5,
        addressZipMax: 9,
        claimNumber: 50,
        insuredName: 50,
        loanNumber: 50,
        mobilePhone: 10,
        pin: 4
    };

    function displayStatesForUS(country: string) : boolean {
        return (country === "US");
    }

    const ValidationSchema = Yup.object().shape({
        claimNumber: Yup.string()
            .trim().required(t("lblClaimNumberRequired")),
        insuredName: Yup.string()
            .trim().required(t("lblInsuredNameRequired")),
        loanNumber: Yup.string()
            .trim().required(t("lblLoanNumberRequired")),
        mailingAddressSameAsPropertyAddress: Yup.boolean(),
        mailingAddress: Yup.object().when("mailingAddressSameAsPropertyAddress", {
            is: false,
            then: () => Yup.object().shape({
                countryISO: Yup.string().trim()
                    .required(t("lblAddressCountryRequired")),
                addressLine1: Yup.string().trim()
                    .required(t("lblAddressLine1Required")),
                city: Yup.string().trim()
                    .required(t("lblAddressCityRequired")),
                state: Yup.object().when("countryISO", {
                    is: (value) => (value === undefined || value === "" || value === "US"),
                    then: () => Yup.string().trim()
                        .required(t("lblAddressStateRequired")),
                    otherwise: () => Yup.string().trim()
                        .required(t("lblAddressStateProvinceRequired"))
                }),
                postalCode: Yup.object().when("countryISO", {
                    is: (value) => (value === undefined || value === "" || value === "US"),
                    then: () => Yup.string().trim()
                        .required(t("lblAddressZipRequired"))
                        .test("mailingAddressPostalCode-format", t("lblAddressZipInvalid"), (value, context) => {
                            return (!!value && (value.length === 5 || value.length === 9));
                        }),
                    otherwise: () => Yup.string().trim()
                        .required(t("lblAddressPostalCodeRequired"))
                })
             })
        }),
        sendSMS: Yup.boolean(),
        //https://github.com/jquense/yup?tab=readme-ov-file#schemawhenkeys-string--string-builder-object--values-any-schema--schema-schema
        mobilePhone: Yup.string().when('sendSMS', {
                    is: true,
                    then: (schema) => //schema means the validation logic for that field, in this case, the Yup.string() instance used to define rules for mobilePhone 
                        schema
                            .required(t("lblMobilePhoneRequired"))
                            .length(fieldLength.mobilePhone, t("lblMobilePhoneInvalid")),
                    otherwise: (schema) =>
                        schema
                            .length(fieldLength.mobilePhone, t("lblMobilePhoneInvalid")),
        }),
        pin: Yup.string().nullable()
            .min(fieldLength.pin, t("lblPinInvalid"))
            .max(fieldLength.pin, t("lblPinInvalid")),
        propertyAddress: Yup.object().shape({
            addressLine1: Yup.string()
                .trim().required(t("lblAddressLine1Required")),
            city: Yup.string()
                .trim().required(t("lblAddressCityRequired")),
            state: Yup.string()
                .trim().required(t("lblAddressStateRequired")),
            postalCode: Yup.string()
                .trim().required(t("lblAddressZipRequired"))
                .test("propertyAddressPostalCode-format", t("lblAddressZipInvalid"), (value, context) => {
                    return (!!value && (value.length === 5 || value.length === 9));
                })
        })
    });

    function onChangeCollapse(key: string | string[]) {
        if (Array.isArray(key)) {
            if (key.length === 0) {
                setActiveCollapseKey([]); // No panel is active
            }
            else {
                const latestKey = key.slice(-1)[0]; // Get the last active key, since the key contains an array of active keys.
                setActiveCollapseKey([latestKey]); // Only keep the current active key.
            }
        }
    };

    function handleSubmit(values, setSubmitting) {
        setSubmitting(false);   // MUST invoke this before updating any parent props or the form submit happens twice!
        extendSession();

        // Copy form values into stagingClaim while removing properties we don't want to submit
        const {mailingAddressSameAsPropertyAddress, pin, ...stagingClaim} = values;
        
        // Add props that were not on the form but are needed to submit
        stagingClaim.encodedClientId = props.stagingClaimData.encodedClientId;
        stagingClaim.lD_StagingClaimID = props.stagingClaimData.lD_StagingClaimID;
        stagingClaim.ssoUserId = props.stagingClaimData.ssoUserId;
        
        // PIN is a string here but an INT in the backend ... convert before sending
        stagingClaim.pin = parseInt(pin);

        if (mailingAddressSameAsPropertyAddress) {
            stagingClaim.mailingAddress = stagingClaim.propertyAddress;
        }

        const jsonData = JSON.stringify(stagingClaim);
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/StagingClaim");

        console.log("Update User Registration at " + url);

        putJson(url, accessToken, jsonData)
        .then((response) => {
            if (!response.ok) { throw new Error(response.status + " - " + response.statusText)}
            return response.json() as Promise<PostResponse>;
        })
        .then(response => {
            if (response.success) {
                localStorage.removeItem(CacheKey.CLIENT_ID);
                localStorage.setItem(CacheKey.LOSS_DRAFTS_STAGING_CLAIM_SUBMITTED, "true");
                props.hideModal();
                props.refreshData();
                if (stagingClaim.mobilePhone !== userProfile.mobilePhone || stagingClaim.sendSMS !== userProfile.sendSMS || stagingClaim.consentToESignature !== userProfile.consentToESignature) {
                    refreshUserProfile();
                }
                if (props.allowSuccessNotification) {
                    SuccessNotification(t('lblUpdateSuccessTitle'), t('lblUpdateSuccessBody'));
                }
            }
            else {
                setErrorMsg(t('lblUpdateFailure'));
            }
        })
        .catch(() => {
            setErrorMsg(t('lblUpdateFailure'));
        })
    }

    function renderForm() {
        return (
            <Formik
                initialValues ={{
                    claimNumber: props.stagingClaimData.claimNumber,
                    consentToESignature: userProfile.consentToESignature,
                    insuredName: props.stagingClaimData.insuredName,
                    loanNumber: props.stagingClaimData.loanNumber,
                    sendSMS: userProfile.sendSMS,
                    mailingAddress: props.stagingClaimData.mailingAddress,
                    mailingAddressSameAsPropertyAddress: props.stagingClaimData.mailingAddressSameAsPropertyAddress,
                    mobilePhone: userProfile.mobilePhone,
                    propertyAddress: props.stagingClaimData.propertyAddress,
                    pin: formatPin(props.stagingClaimData.pin)
                }}
                validationSchema={ValidationSchema}
                onSubmit={(values, { setSubmitting }) => {
                    handleSubmit(values, setSubmitting);
                }}
            >
            {
                ({ touched, values, isSubmitting, setFieldValue, submitForm }) => (
                    <Form>
                        <Collapse defaultActiveKey={["p"]} destroyOnHidden={true} activeKey={activeCollapseKey} onChange={onChangeCollapse} items={[
                            {
                                key: "p",
                                label: t('lblPropertyAddress'),
                                showArrow: true,
                                children:
                                    <div className="column-container space-between">
                                        <div>
                                            <label htmlFor="propertyAddress.addressLine1">{t('lblAddressLine1')}</label>
                                            <br />
                                            <Field id="propertyAddress.addressLine1" name="propertyAddress.addressLine1" type="text" className="input width-input-large" placeholder={t('lblAddressLine1')} maxLength={fieldLength.addressLine1} data-lpignore="true" />
                                            <br />
                                            <ErrorMessage name="propertyAddress.addressLine1" component="div" className="error width-input-large" />
                                            <br />
                                        </div>
                                        <div>
                                            <label htmlFor="propertyAddress.addressLine2">{t('lblAddressLine2')}</label>
                                            <br />
                                            <Field id="propertyAddress.addressLine2" name="propertyAddress.addressLine2" type="text" className="input width-input-large" placeholder={t('lblAddressLine2')} maxLength={fieldLength.addressLine2} data-lpignore="true" />
                                            <br />
                                            <ErrorMessage name="propertyAddress.addressLine2" component="div" className="error width-input-large" />
                                            <br />
                                        </div>
                                        <div>
                                            <label htmlFor="propertyAddress.city">{t('lblAddressCity')}</label>
                                            <br />
                                            <Field id="propertyAddress.city" name="propertyAddress.city" type="text" className="input width-input-large" placeholder={t('lblAddressCity')} maxLength={fieldLength.addressCity} data-lpignore="true" />
                                            <br />
                                            <ErrorMessage name="propertyAddress.city" component="div" className="error width-input-large" />
                                            <br />
                                        </div>
                                        <div>
                                            <label id="propertyAddressStateLabel">{t('lblAddressState')}</label>
                                            <br />
                                            <div className='width-input-large'>
                                                <Field name="propertyAddress.state" ariaLabelledBy="propertyAddressStateLabel" component={SelectFormField} options={props.usStateSelectOptions.options} />
                                            </div>
                                            <ErrorMessage name="propertyAddress.state" component="div" className="error width-input-large" />
                                            <br />
                                        </div>
                                        <div>
                                            <label htmlFor="propertyAddress.postalCode">{t('lblAddressZip')}</label>
                                            <br />
                                            <Field name="propertyAddress.postalCode">
                                            {
                                                ({ field }) => {
                                                return <IMaskInput mask="00000-0000"
                                                    {...field}
                                                    id="propertyAddress.postalCode"
                                                    className="input width-input-large"
                                                    data-lpignore="true"
                                                    placeholder={t('lblAddressZip')} 
                                                    lazy={false} // Show full mask by default
                                                    unmask={true}
                                                    onAccept={(value) => {
                                                        setFieldValue(field.name, value.replace(zipMaskRegExp, ''));
                                                    }} />
                                                }
                                            }
                                            </Field>
                                            <br />
                                            <ErrorMessage name="propertyAddress.postalCode" component="div" className="error width-input-large" />
                                            <br />
                                        </div>
                                    </div>
                            },
                            {
                                key: "m",
                                label: "Mailing Address",
                                showArrow: true,
                                children:
                                    <div>
                                        <label htmlFor="mailingAddressSameAsPropertyAddress">
                                            <Field id="mailingAddressSameAsPropertyAddress" name="mailingAddressSameAsPropertyAddress" type="checkbox" />
                                            &nbsp;{t('lblMailingAddressSameAsPropertyAddress')}
                                        </label>
                                        <br /><br />
                                        {
                                            !values.mailingAddressSameAsPropertyAddress && (
                                                <div className="column-container space-between">
                                                <div>
                                                    <label htmlFor="mailingAddress.addressLine1">{t('lblAddressLine1')}</label>
                                                    <br />
                                                    <Field id="mailingAddress.addressLine1" name="mailingAddress.addressLine1" type="text" className="input width-input-large" placeholder={t('lblAddressLine1')} maxLength={fieldLength.addressLine1} data-lpignore="true" />
                                                    <br />
                                                    <ErrorMessage name="mailingAddress.addressLine1" component="div" className="error width-input-large" />
                                                    <br />
                                                </div>
                                                <div>
                                                    <label htmlFor="mailingAddress.addressLine2">{t('lblAddressLine2')}</label>
                                                    <br />
                                                    <Field id="mailingAddress.addressLine2" name="mailingAddress.addressLine2" type="text" className="input width-input-large" placeholder={t('lblAddressLine2')} maxLength={fieldLength.addressLine2} data-lpignore="true" />
                                                    <br />
                                                    <ErrorMessage name="mailingAddress.addressLine2" component="div" className="error width-input-large" />
                                                    <br />
                                                </div>
                                                <div>
                                                    <label htmlFor="mailingAddress.city">{t('lblAddressCity')}</label>
                                                    <br />
                                                    <Field id="mailingAddress.city" name="mailingAddress.city" type="text" className="input width-input-large" placeholder={t('lblAddressCity')} maxLength={fieldLength.addressCity} data-lpignore="true" />
                                                    <br />
                                                    <ErrorMessage name="mailingAddress.city" component="div" className="error width-input-large" />
                                                    <br />
                                                </div>
                                                {
                                                    displayStatesForUS(values.mailingAddress.countryISO) && 
                                                    (
                                                        <>
                                                        <div>
                                                            <label id="mailingAddressStateLabel">{t('lblAddressState')}</label>
                                                            <br />
                                                            <div className='width-input-large'>
                                                                <Field name="mailingAddress.state" ariaLabelledBy="lblAddressState" component={SelectFormField} options={props.usStateSelectOptions.options} />
                                                            </div>
                                                            <ErrorMessage name="mailingAddress.state" component="div" className="error width-input-large" />
                                                            <br />
                                                        </div>
                                                        <div>
                                                            <label htmlFor="mailingAddress.postalCode">{t('lblAddressZip')}</label>
                                                            <br />
                                                            <Field name="mailingAddress.postalCode">
                                                            {
                                                                ({ field }) => {
                                                                return <IMaskInput mask="00000-0000"
                                                                    {...field}
                                                                    id="mailingAddress.postalCode"
                                                                    className="input width-input-large"
                                                                    data-lpignore="true"
                                                                    placeholder={t('lblAddressZip')} 
                                                                    lazy={false} // Show full mask by default
                                                                    unmask={true}
                                                                    onAccept={(value: string) => {
                                                                        setFieldValue(field.name, value.replace(zipMaskRegExp, ''));
                                                                    }} />
                                                                }
                                                            }
                                                            </Field>
                                                            <br />
                                                            <ErrorMessage name="mailingAddress.postalCode" component="div" className="error width-input-large" />
                                                            <br />
                                                        </div>
                                                        </>
                                                    )
                                                }
                                                {
                                                    !displayStatesForUS(values.mailingAddress.countryISO) && 
                                                    (
                                                        <>
                                                        <div>
                                                            <label htmlFor="mailingAddress.state">{t('lblAddressStateProvince')}</label>
                                                            <br />
                                                            <Field name="mailingAddress.state">
                                                            {
                                                                ({ field }) => {
                                                                return <IMaskInput mask="AAAAAAAAAAAAAAAAAAAAAAAAA" // 25 characters
                                                                    definitions={{
                                                                        A: /[A-Za-z0-9 ,\-]/,
                                                                    }}
                                                                    id="mailingAddress.state"
                                                                    className="input width-input-large"
                                                                    placeholder={t('lblAddressStateProvince')} 
                                                                    onAccept={(value: string) => {
                                                                        setFieldValue(field.name, value);
                                                                    }} />
                                                                }
                                                            }
                                                            </Field>
                                                            <br />
                                                            <ErrorMessage name="mailingAddress.state" component="div" className="error width-input-large" />
                                                            <br />
                                                        </div>
                                                        <div>
                                                            <label htmlFor="mailingAddress.postalCode">{t('lblAddressPostalCode')}</label>
                                                            <br />
                                                            <Field name="mailingAddress.postalCode">
                                                            {
                                                                ({ field }) => {
                                                                    return <IMaskInput mask="AAAAAAAAAA"
                                                                    definitions={{
                                                                            A: /[A-Za-z0-9 \-]/,
                                                                    }}
                                                                    {...field}
                                                                    id="mailingAddress.postalCode"
                                                                    className="input width-input-large"
                                                                    data-lpignore="true"
                                                                    placeholder={t('lblAddressPostalCode')} 
                                                                    lazy={false}
                                                                    unmask={true}
                                                                    onAccept={(value: string) => {
                                                                        setFieldValue(field.name, value);
                                                                    }} />
                                                                }
                                                            }
                                                            </Field>
                                                            <br />
                                                            <ErrorMessage name="mailingAddress.postalCode" component="div" className="error width-input-large" />
                                                            <br />
                                                        </div>
                                                        </>
                                                    )
                                                }
                                                <div>
                                                    <label id="mailingAddressCountryLabel">{t('lblAddressCountry')}</label>
                                                    <br />
                                                    <div className='width-input-large'>
                                                        <Field name="mailingAddress.countryISO" ariaLabelledBy="mailingAddressCountryLabel" component={SelectFormField} options={props.countrySelectOptions.options} />
                                                    </div>
                                                    <ErrorMessage name="mailingAddress.countryISO" component="div" className="error width-input-large" />
                                                    <br />
                                                </div>
                                            </div>
                                            )
                                        }
                                    </div>
                            }
                        ]}>
                        </Collapse>
                        <br />
                        <div className="column-container space-between">
                            <div>
                                <label htmlFor="insuredName">{t('lblInsuredName')}</label>
                                <br />
                                <Field id="insuredName" name="insuredName" type="text" className="input width-input-large" placeholder={t('lblInsuredName')} maxLength={fieldLength.insuredName} data-lpignore="true" />
                                <br />
                                <ErrorMessage name="insuredName" component="div" className="error width-input-large" />
                                <br />
                            </div>
                            <div>
                                <label htmlFor="pin">{t('lblPin')}</label>
                                <br />
                                <Field name="pin">
                                {
                                    ({ field }) => {
                                        return <IMaskInput  mask="0000"
                                            {...field}
                                            id="pin"
                                            className="input width-input-large"
                                            data-lpignore="true"
                                            placeholder={t('lblPin')}
                                            lazy={false} // Show full mask by default
                                            unmask={true}
                                            onAccept={(value: string) => {
                                                setFieldValue(field.name, value.replace(pinMaskRegExp, ''));
                                            }} />
                                    }
                                }
                                </Field>
                                <br />
                                <ErrorMessage name="pin" component="div" className="error width-input-large" />
                                <br />
                            </div>
                        </div>
                        <div className="column-container space-between">
                            <div>
                                <label htmlFor="loanNumber">{t('lblLoanNumber')}</label>
                                <br />
                                <Field id="loanNumber" name="loanNumber" type="text" className="input width-input-large" placeholder={t('lblLoanNumber')} maxLength={fieldLength.loanNumber} data-lpignore="true" />
                                <br />
                                <ErrorMessage name="loanNumber" component="div" className="error width-input-large" />
                                <br />
                            </div>
                            <div>
                                <label htmlFor="claimNumber">{t('lblClaimNumber')}</label>
                                <br />
                                <Field id="claimNumber" name="claimNumber" type="text" className="input width-input-large" placeholder={t('lblClaimNumber')} maxLength={fieldLength.claimNumber} data-lpignore="true" />
                                <br />
                                <ErrorMessage name="claimNumber" component="div" className="error width-input-large" />
                                <br />
                            </div>
                        </div>
                        <div>
                            <div className="column-container space-between">
                                <div>
                                <label htmlFor="sendSMS">{t('Profile:lblSmsConsent')}</label>
                                <br />
                                <Field id="sendSMS" name="sendSMS" type="checkbox"></Field>
                                &nbsp;&nbsp;{t('Profile:lblSmsLegalese')}<br />
                                <a href="https://www.bbrown.com/us/legal-notices" target="_blank" rel="noopener noreferrer">{t('Layout:lblLegal')}</a>
                                &nbsp;/&nbsp;
                                <a href="https://www.bbrown.com/us/privacy-statement" target="_blank" rel="noopener noreferrer">{t('Layout:lblPrivacy')}</a>
                                </div>
                            </div>
                        </div>
                        <br />
                         <div>
                        <div className="column-container space-between">
                        <div>
                                <label htmlFor="mobilePhone">{t('lblMobilePhone')}</label>
                                <br />
                                <Field name="mobilePhone">
                                {
                                    ({ field }) => {
                                        return <IMaskInput mask="(X00) 000-0000"
                                            definitions={{
                                                X: {
                                                    mask: /^[2-9]$/,  // Ensures area code doesn't start with 0 or 1
                                                }
                                            }}
                                            {...field}
                                            id="mobilePhone"
                                            className="input width-input-large"
                                            data-lpignore="true"
                                            placeholder={t('lblMobilePhone')}
                                            lazy={false} // Show full mask by default
                                            unmask={true}
                                            onAccept={(value: string) => {
                                                setFieldValue(field.name, value.replace(phoneNumberMaskRegExp, ''));
                                            }} />
                                    }
                                }
                                </Field>
                                <br />
                                <ErrorMessage name="mobilePhone" component="div" className="error width-input-large" />
                                <br />
                            </div>
                            </div>
                        <div />
                        </div>
                        { 
                            userProfile.allowESignature && 
                            (
                                <div>
                                    <div className="column-container space-between">
                                        <div>
                                            <label htmlFor="consentToESignature">{t('Profile:lblESignatureConsent')}</label>
                                            <br />
                                            <Field id="consentToESignature" name="consentToESignature" type="checkbox" checked={values.consentToESignature}></Field>
                                            &nbsp;&nbsp;{t('Profile:lblESignatureLegalese')}
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                        <br />
                        <Button type="default" className="button" onClick={() => props.hideModal()}>{t('Form:btnCancel')}</Button>
                        &nbsp;&nbsp;
                        <Button type="primary" className="button submit" disabled={isSubmitting} onClick={submitForm}>{t('lblUpdate')}</Button>
                        <br /><br />
                        <div className="error">{errorMsg}</div>
                    </Form>
                )
            }
            </Formik>
        );
    }

    return (
        <Modal open={true} 
               title={t('lblUpdate')} 
               footer={null}
               onCancel={() => props.hideModal()}
               centered={true}
               destroyOnHidden={true}
        >
            <hr />
            {renderForm()}
        </Modal>
    );
}

export default RegistrationUpdate;
